import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

/**
 * Phase 5.C — admin view of a residential site.
 *
 * Mounted by ``SiteMap.js`` when the loaded site reports
 * ``type === 'residential'``. A private home doesn't have a floor plan
 * to drag gates onto, so the industrial canvas + edit toolbar + group
 * controls are all gone. What's left is a small list of gates with a
 * "+ שער" button — enough to seed the customer's residential UI with
 * real data while the BLE install wizard is still being built.
 *
 * Each row links into the existing admin gate detail page; the gate's
 * paired-Shelly state (Phase 5.A: control_device_id + lock_device_id)
 * lives on the same row so Huri staff can see at a glance whether
 * both devices are wired up.
 */
function ResidentialSiteAdmin({ site, onReload }) {
  const [showAdd, setShowAdd] = useState(false);

  return (
    <>
      <div className="breadcrumb">
        <Link to="/customers">לקוחות</Link>
        <span>/</span>
        <Link to={`/customers/${site.customer_id}`}>חזרה ללקוח</Link>
        <span>/</span>
        <span>{site.name}</span>
      </div>

      <div className="info-card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>
            {site.name}
            <span style={{
              marginInlineStart: '0.6rem', fontSize: '0.7rem', fontWeight: 600,
              color: '#9a3412', background: '#FFEDD5', borderRadius: '999px',
              padding: '0.15rem 0.6rem', verticalAlign: 'middle',
            }}>אתר ביתי</span>
          </h3>
          <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>
            + שער חדש
          </button>
        </div>
        {site.address && (
          <div style={{ color: '#6b7280', fontSize: '0.9rem', marginTop: '0.35rem' }}>
            {site.address}
          </div>
        )}
        <div style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: '0.6rem', lineHeight: 1.55 }}>
          באתר ביתי אין מפה — שער הוא יחידה עצמאית עם זוג רכיבי Shelly
          (2PM ל-control + i1 ל-lock). אשף ההתקנה הביתי לטכנאי
          יותקן בהמשך פאזה 5; בינתיים אפשר לפתוח את השער הלוגי כאן
          כדי שהוא יופיע אצל הלקוח.
        </div>
      </div>

      <div className="section-header"><h3>שערים</h3></div>
      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th>שם</th>
              <th>רכיב control</th>
              <th>רכיב lock</th>
              <th>סטטוס</th>
            </tr>
          </thead>
          <tbody>
            {(site.gates || []).map(g => (
              <tr key={g.id}>
                <td style={{ fontWeight: 500 }}>{g.name}</td>
                <td><DeviceBadge present={!!g.control_device_id} label="2PM" /></td>
                <td><DeviceBadge present={!!g.lock_device_id} label="i1" /></td>
                <td>
                  {(g.control_device_id && g.lock_device_id)
                    ? <span style={{ color: '#15803D', fontSize: '0.78rem', fontWeight: 600 }}>מוכן</span>
                    : <span style={{ color: '#92400E', fontSize: '0.78rem', fontWeight: 600 }}>ממתין להתקנה</span>}
                </td>
              </tr>
            ))}
            {(!site.gates || site.gates.length === 0) && (
              <tr><td colSpan="4" style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>
                אין שערים עדיין
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <AddResidentialGateModal
          siteId={site.id}
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); onReload(); }}
        />
      )}
    </>
  );
}

function DeviceBadge({ present, label }) {
  return (
    <span style={{
      fontSize: '0.72rem',
      fontWeight: 600,
      padding: '0.15rem 0.55rem',
      borderRadius: '999px',
      background: present ? '#DCFCE7' : '#F1F5F9',
      color: present ? '#15803D' : '#64748B',
    }}>
      {present ? `${label} ✓` : `${label} —`}
    </span>
  );
}

function AddResidentialGateModal({ siteId, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`/api/admin/sites/${siteId}/gates`, { name });
      onCreated();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה ביצירת שער');
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" onClick={e => e.stopPropagation()} onSubmit={submit}>
        <h2>שער ביתי חדש</h2>
        {error && <div className="login-error">{error}</div>}
        <div className="form-group">
          <label>שם השער</label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="לדוגמה: שער חצר"
            required
            autoFocus
          />
        </div>
        <div style={{
          fontSize: '0.82rem',
          color: '#64748b',
          background: '#F8FAFC',
          borderRadius: '8px',
          padding: '0.7rem 0.85rem',
          marginBottom: '1rem',
          lineHeight: 1.55,
        }}>
          הרכיבים (2PM ל-control + i1 ל-lock) יוצמדו בעצמם לשער הזה
          אחרי שאשף ההתקנה הביתי יבנה. בינתיים השער יופיע בממשק
          הלקוח אבל בלי חיבור פיזי.
        </div>
        <div className="modal-actions">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'יוצר…' : 'צור שער'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            ביטול
          </button>
        </div>
      </form>
    </div>
  );
}

export default ResidentialSiteAdmin;
